import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AddContactSheet } from '../components/contacts/AddContactSheet.tsx';
import { useFacilityPage } from '../api/facilityPage.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { FacilityMap } from '../components/FacilityMap.tsx';
import { FacilityEditor, FacilityEdits } from '../components/FacilityEditor.tsx';
import { HeldMarker, StatusTag } from '../components/StatusTag.tsx';
import { formatDate } from '../dates.ts';

/**
 * A building's page (8 October): what it is, where it is, the clients placed
 * there, and its people.
 *
 * THE CLIENTS ARE THE CALLER'S OWN. A case manager sees the clients they could
 * already see (the Director, the department's), and everyone else placed there
 * is a NUMBER: "4 other clients of the agency are placed here". Never a name,
 * a status or a link. The number is what a trip is planned around, or shared
 * with a colleague, and it is what intake already shows anyone adding a
 * building. Deceased clients are not counted: nobody visits them.
 *
 * Contacts are the building's people (089), open to anyone who can see the
 * building, and added by those who may record a placement (contacts module).
 */
export function FacilityPage() {
  const { id } = useParams<{ id: string }>();
  const page = useFacilityPage(id);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [adding, setAdding] = useState(false);

  if (page.error && !page.data) {
    return (
      <Shell>
        {page.error instanceof ApiError && page.error.status === 404 ? (
          <div className="p-6">
            <h1 className="text-lg font-bold text-neutral-900">No such facility</h1>
            <Link to="/facilities" className="mt-4 inline-flex min-h-[44px] items-center font-semibold text-neutral-800 underline">
              Find a facility
            </Link>
          </div>
        ) : (
          <LoadFailure what="This facility" error={page.error} onRetry={() => void page.refetch()} retrying={page.isFetching}
                       badLink={{ says: 'This is not a valid building link.', back: { to: '/facilities', label: 'Back to the buildings' } }} />
        )}
      </Shell>
    );
  }
  if (!page.data) return <Shell><p className="px-4 py-10 text-center text-neutral-600">Loading…</p></Shell>;

  const { facility: f, clients, others, contacts } = page.data;
  const address = [f.street_address, [f.city, f.state].filter(Boolean).join(', '), f.zip].filter(Boolean).join(' · ');

  return (
    <Shell>
      <h1 className="panel-bar !text-[1.0625rem] !normal-case !tracking-normal">{f.facility_name}</h1>
      <header className="border-b border-line px-4 pt-1 pb-3 lg:px-5">
        <Link to="/facilities" className="link inline-flex min-h-[44px] items-center text-sm font-semibold">
          ‹ Facilities
        </Link>
        <p className="mt-0.5 text-[0.9375rem] text-neutral-700">
          {[f.residence_type_label ?? f.facility_type ?? 'Type not recorded', f.is_active ? null : 'No longer in use']
            .filter(Boolean).join(' · ')}
        </p>
        <p className="text-[0.9375rem] text-neutral-700">{address || 'No address recorded'}</p>
        {(f.neighbourhood || f.county || f.phone || f.contact_email) && (
          <p className="text-sm text-neutral-600">
            {[f.neighbourhood, f.county ? `${f.county} County` : null, f.phone].filter(Boolean).join(' · ')}
            {f.contact_email && (
              <>
                {(f.neighbourhood || f.county || f.phone) && ' · '}
                <a href={`mailto:${f.contact_email}`} className="underline">{f.contact_email}</a>
              </>
            )}
          </p>
        )}
        {f.notes && <p className="mt-1 text-sm whitespace-pre-line text-neutral-700">{f.notes}</p>}
        <FacilityEdits id={f.id} times={f.times_edited} />
        {saved && !editing && <p role="status" className="mt-1 text-sm font-semibold text-neutral-800">Saved, and kept as a new version.</p>}
        {page.data.may_edit && !editing && (
          <button type="button" onClick={() => { setSaved(false); setEditing(true); }}
                  className="mt-2 min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100">
            Edit details
          </button>
        )}
      </header>
      {editing && (
        <FacilityEditor facility={f} onDone={(ok) => { setEditing(false); setSaved(ok); }} />
      )}

      <div className="grid gap-6 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:px-5">
        <section aria-labelledby="placed-here">
          <h2 id="placed-here" className="text-sm font-bold tracking-[0.1em] text-neutral-500 uppercase">
            Placed here
          </h2>
          {clients.length === 0 ? (
            <p className="mt-2 text-[0.9375rem] text-neutral-700">
              {others > 0 ? 'None of your clients is placed here.' : 'Nobody the agency serves is placed here now.'}
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-neutral-100 rounded-lg border border-neutral-200">
              {clients.map((c) => (
                <li key={c.client_id}>
                  <Link to={`/clients/${c.client_id}`} state={{ from: f.facility_name }}
                        className="block min-h-[52px] px-3 py-2 hover:bg-neutral-50 active:bg-neutral-100">
                    <div className="flex min-w-0 items-baseline gap-2">
                      <p className="min-w-0 truncate font-semibold text-neutral-900">{c.display_name}</p>
                      <StatusTag status={c.legal_status} />
                      <HeldMarker held={c.held_at_load} />
                      <span className="ml-auto shrink-0 font-mono text-sm text-neutral-500 tabular-nums">{c.client_ref}</span>
                    </div>
                    <p className="truncate text-sm text-neutral-600">
                      {[c.case_manager ?? 'No case manager',
                        c.started_on ? `here since ${formatDate(c.started_on)}` : c.started_on_unknown ? 'here since a date not known' : null]
                        .filter(Boolean).join(' · ')}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {others > 0 && (
            <p className="mt-2 text-[0.9375rem] text-neutral-700">
              {clients.length > 0 ? 'And ' : ''}
              {others} other {others === 1 ? 'client' : 'clients'} of the agency {others === 1 ? 'is' : 'are'} placed
              here, held by colleagues.
            </p>
          )}

          <div className="mt-6 flex items-center gap-2">
            <h2 className="flex-1 text-sm font-bold tracking-[0.1em] text-neutral-500 uppercase">People at this facility</h2>
            {page.data.may_add_contacts && (
              <button type="button" onClick={() => setAdding(true)}
                      className="min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50">
                Add a contact
              </button>
            )}
          </div>
          {adding && <AddContactSheet target={{ facility_id: f.id }} onClose={() => setAdding(false)} />}
          {contacts.length === 0 ? (
            <p className="mt-2 text-[0.9375rem] text-neutral-600">
              No contacts are recorded for this facility yet.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-neutral-100 rounded-lg border border-neutral-200">
              {contacts.map((p) => (
                <li key={p.link_id} className="px-3 py-2">
                  <p className="font-semibold text-neutral-900">
                    {p.name ?? 'Name not recorded'}
                    {p.is_primary && <span className="ml-2 text-xs font-semibold text-neutral-500">Main contact</span>}
                  </p>
                  <p className="text-sm text-neutral-600">
                    {[p.role_label, p.job_title].filter(Boolean).join(' · ') || 'Role not recorded'}
                  </p>
                  {(p.phone || p.email) && (
                    <p className="text-sm text-neutral-700">
                      {p.phone && <a href={`tel:${p.phone}`} className="inline-flex min-h-[44px] items-center underline">{p.phone}</a>}
                      {p.phone && p.email && ' · '}
                      {p.email && <a href={`mailto:${p.email}`} className="inline-flex min-h-[44px] items-center underline">{p.email}</a>}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Map">
          {f.latitude != null && f.longitude != null ? (
            <FacilityMap latitude={f.latitude} longitude={f.longitude} name={f.facility_name} />
          ) : (
            <p className="rounded-lg border border-dashed border-neutral-300 px-3 py-10 text-center text-neutral-600">
              {f.locating ? 'Finding this address on the map.' : 'Not located on the map yet.'}
            </p>
          )}
        </section>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <div className="panel">{children}</div>
      </div>
    </div>
  );
}
